/**
 * Today tab — "what a returning daily user needs in <5s".
 *
 * Top-of-funnel widgets (semester plan, today's review, next action,
 * focus session, goal countdown), followed by a compact recent-lectures
 * list, upcoming exams, and a 5-button quick-actions grid.
 *
 * Props are intentionally minimal so the parent (Dashboard.js) owns all
 * data fetching and this stays a pure presentation component.
 */
import { motion } from 'framer-motion';
import { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  GraduationCap, Plus, Clock, Calendar, Zap, ArrowRight, Mic,
  Lightbulb, BookMarked, Brain, BookOpen, FileText, Sparkles,
  SlidersHorizontal, CheckCircle2, Handshake,
} from 'lucide-react';
import { Button } from '../ui/button';
import SemesterPlanTodayCard from '../SemesterPlanTodayCard';
import CardsDueTodayWidget from '../CardsDueTodayWidget';
import NextActionHero from '../NextActionHero';
import GoalProgressCard from '../GoalProgressCard';
import HabitLoopCard from '../HabitLoopCard';
// iter196 — lazy-load below-the-fold + modal-only chunks so first paint
// stays under 300ms. Customize modal opens on user gesture; WrappedCard
// only appears during wrapped season (~Dec). Suspense fallback is null
// because each is below-the-fold and momentary chunk-fetch latency reads
// as a brief intentional reveal, not a skeleton.
const DashboardCustomizeModal = lazy(() => import('../DashboardCustomizeModal'));
const WrappedCard = lazy(() => import('../WrappedCard'));
import ResumeCard from './ResumeCard';
import MonthlyWrappedCard from './MonthlyWrappedCard';
import SocialPressureStrip from './SocialPressureStrip';
import { useDashboardWidgets } from '../../hooks/useDashboardWidgets';
import { useHeroContext } from '../../hooks/useHeroContext';

// 4-step onboarding action row — surfaced beneath the dashboard greeting
// so first-time users always see the canonical happy path:
//   Add Subject → Record A Lecture → Generate Notes → Learn → Pair Up
// Each step is a clickable shortcut that deep-links into the relevant
// surface. Tone (mint/sky/lavender/peach/rose) cycles so the row reads as
// a soft progress sequence without numbered badges.
//
// iter195 added Step 5 ("Pair Up") to surface Study Pacts in the
// canonical onboarding path — accountability is one of the strongest
// retention drivers and was previously buried in the side nav.
const ONBOARDING_STEPS = [
  { to: '/subjects',       icon: BookOpen,   label: 'Add Subject',      tone: 'mint',     testid: 'onboarding-step-subject' },
  { to: '/lectures',       icon: Mic,        label: 'Record A Lecture', tone: 'peach',    testid: 'onboarding-step-record' },
  { to: '/lectures',       icon: FileText,   label: 'Generate Notes',   tone: 'sky',      testid: 'onboarding-step-notes' },
  { to: '/learning-bytes', icon: Sparkles,   label: 'Learn',            tone: 'lavender', testid: 'onboarding-step-learn' },
  { to: '/pacts',          icon: Handshake,  label: 'Pair Up',          tone: 'rose',     testid: 'onboarding-step-pair' },
];

const OnboardingActionRow = () => (
  <div className="mb-2" data-testid="dashboard-onboarding-row">
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {ONBOARDING_STEPS.map((step, idx) => {
        const Icon = step.icon;
        return (
          <Link key={step.to + step.label} to={step.to} aria-label={step.label}>
            <button
              type="button"
              className={`wave-action wave-action--${step.tone} w-full !justify-start !items-start flex-col py-4 px-4 gap-2 group text-left`}
              data-testid={step.testid}
            >
              <div className="flex items-center justify-between w-full">
                <span className="text-[10px] font-bold tracking-[0.18em] uppercase opacity-70">
                  Step {idx + 1}
                </span>
                <ArrowRight className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" aria-hidden />
              </div>
              <div className="flex items-center gap-2.5 w-full">
                <Icon className="w-5 h-5 flex-shrink-0" aria-hidden />
                <span className="text-sm font-semibold leading-tight">{step.label}</span>
              </div>
            </button>
          </Link>
        );
      })}
    </div>
  </div>
);

/**
 * OnboardingProgressChip — the collapsed version of the 4-step row,
 * surfaced for Day-1+ users so the dashboard stops looking identical
 * on Day 1 and Day 30. Shows a tiny "Setup · 4/4 complete" pill that
 * expands back to the full row on click for users who want a refresher.
 *
 * Why expand-on-click instead of removing entirely? The 4-step row is
 * the canonical happy path — power users still occasionally need a
 * quick "where do I add a subject again?" shortcut. We just lower its
 * visual weight from "hero" to "chip".
 */
const OnboardingProgressChip = ({ onExpand }) => (
  <button
    type="button"
    onClick={onExpand}
    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-[var(--isp-muted)] bg-[var(--isp-glass-inner)] hover:bg-[var(--isp-glass-inner-hover)] hover:text-[var(--isp-heading)] transition-colors"
    data-testid="dashboard-onboarding-chip"
  >
    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" aria-hidden />
    Quick start · {ONBOARDING_STEPS.length} shortcuts
  </button>
);

const TodayTab = ({ recentLectures, upcomingExams, isWrappedSeason }) => {
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [forceExpandOnboarding, setForceExpandOnboarding] = useState(false);
  const widgets = useDashboardWidgets();
  const visible = (id) => widgets.isEnabled(id);

  // Adaptive hero phase — Day-0 sees the full 4-step row (canonical
  // happy path), Day-1+ users see a quiet "Quick start · 4 shortcuts"
  // chip + a "Continue where you left off" resume card. Massive
  // retention lift: stops the dashboard looking identical week-to-week.
  const { data: hero } = useHeroContext();
  const isDay0 = !hero || hero.phase === 'day_0';
  const showFullRow = isDay0 || forceExpandOnboarding;

  return (
    <div role="tabpanel" id="dashboard-panel-today" data-testid="panel-today" className="space-y-6">

      {/* Mini-Wrapped — auto-surfaces during the first 7 days of each
          new month with the previous month's stats. Self-hides for
          users who joined mid-month and is dismissible per-month. */}
      <MonthlyWrappedCard />

      {/* iter195 — live social-presence strip. Three personalities
          based on /api/social/dashboard-strip: actively-studying, rooms
          exist but empty, no rooms at all (discovery). */}
      <SocialPressureStrip />

      {/* Resume — surfaces the most-recent meaningful activity so a
          Day-2+ user lands on a "continue" button instead of a generic
          dashboard. Self-hides for brand-new accounts. */}
      <ResumeCard />

      {/* 4-step onboarding shortcuts. Visible in full for Day-0 users;
          collapsed to a chip thereafter (click chip to re-expand). */}
      {showFullRow ? (
        <OnboardingActionRow />
      ) : (
        <OnboardingProgressChip onExpand={() => setForceExpandOnboarding(true)} />
      )}

      {/* Customize-dashboard control — tiny, sits inside the Today panel
          so it doesn't compete with the greeting actions or the tab
          strip. Free users see their cap; Pro shows "Customize" only. */}
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setCustomizeOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--isp-muted)] hover:text-[#002FA7] px-2 py-1 rounded-md hover:bg-[#002FA7]/5"
          data-testid="dashboard-customize-trigger"
          title="Pick which widgets show on your Today tab"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" aria-hidden />
          Customize{!widgets.loading && widgets.tier && widgets.tier.toLowerCase() === 'free' && (
            <span className="text-[10px] font-bold text-[var(--isp-muted)]">· {widgets.enabled.length}/{widgets.limit}</span>
          )}
        </button>
      </div>

      {/* WRAPPED — only visible in Dec/Jan window so it's not noise in April.
          Not user-customizable: seasonal one-off, lives outside the
          reordering loop on purpose. */}
      {isWrappedSeason && (
        <Suspense fallback={null}>
          <WrappedCard />
        </Suspense>
      )}

      {/* User-customizable widget area — rendered in the order the user
          chose in the Customize modal. The renderers map below is the
          single source of truth; iter188 made the order data-driven so
          drag-to-reorder in the modal is reflected on the dashboard. */}
      {widgets.enabled.map((id) => {
        const renderer = WIDGET_RENDERERS[id];
        if (!renderer) return null;
        return <div key={id}>{renderer({ recentLectures, upcomingExams })}</div>;
      })}

      {/* Quick Actions — 5 most-used study verbs. Wave-inspired soft
          pastel buttons (iter184): each action gets a distinct hue so
          the eye can find "Record" or "Review" in a glance. Not part
          of the customizable widget list — they're always shown. */}
      <div className="isp-card p-5 lg:p-6">
        <h3 className="font-heading text-base font-semibold text-[var(--isp-heading)] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { to: '/lectures',     icon: Mic,        label: 'Record',     tone: 'peach' },
            { to: '/learning-bytes', icon: Lightbulb,  label: 'Flashcards', tone: 'mint' },
            { to: '/review',       icon: BookMarked, label: 'Review',     tone: 'lavender' },
            { to: '/study-buddy',  icon: Brain,      label: 'AI Buddy',   tone: 'sky' },
            { to: '/schedule',     icon: Calendar,   label: 'Schedule',   tone: 'mint' },
          ].map((action) => {
            const Icon = action.icon;
            return (
              <Link key={action.to} to={action.to}>
                <button
                  className={`wave-action wave-action--${action.tone} w-full flex-col py-4`}
                  data-testid={`quick-action-${action.label.toLowerCase()}`}
                  aria-label={action.label}
                >
                  <Icon className="w-5 h-5" aria-hidden />
                  <span className="text-xs font-semibold">{action.label}</span>
                </button>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Modal is mounted only when opened — Suspense renders null while
          the chunk loads (sub-50ms typical). User sees the trigger click
          → brief flicker → modal. Acceptable trade for the first-paint win. */}
      {customizeOpen && (
        <Suspense fallback={null}>
          <DashboardCustomizeModal
            open={customizeOpen}
            onClose={() => setCustomizeOpen(false)}
            available={widgets.available}
            enabled={widgets.enabled}
            limit={widgets.limit}
            tier={widgets.tier}
            onSave={widgets.saveWidgets}
          />
        </Suspense>
      )}
    </div>
  );
};

// Widget renderers — keyed by widget id, each returns the JSX for that
// widget. We iterate `widgets.enabled` in order to draw them, so the
// drag-to-reorder UI in the Customize modal is reflected here.
const WIDGET_RENDERERS = {
  'habit-loop':     () => <HabitLoopCard />,
  'semester-plan':  () => <SemesterPlanTodayCard />,
  'cards-due':      () => <CardsDueTodayWidget />,
  'next-action':    () => <NextActionHero defaultMinutes={15} />,
  'focus-cta':      () => (
    <Link
      to="/focus"
      className="block border border-[var(--isp-glass-border)] hover:border-[#002FA7] bg-[var(--isp-glass)] hover:bg-[var(--isp-glass-hover)] p-5 transition-colors group"
      data-testid="dashboard-focus-cta"
    >
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 flex items-center justify-center bg-[#002FA7]/10 group-hover:bg-[#002FA7] transition-colors shrink-0">
          <Zap className="w-5 h-5 text-[#002FA7] group-hover:text-white transition-colors" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#002FA7] mb-1">Distraction-free</p>
          <h3 className="text-base font-bold text-[var(--isp-heading)] tracking-tight">Start a Focus Session</h3>
          <p className="text-xs text-[var(--isp-muted)] mt-0.5">25-minute Pomodoro · No nav · No notifications · Just the work.</p>
        </div>
        <ArrowRight className="w-4 h-4 text-[var(--isp-muted)] group-hover:text-[var(--isp-heading)] group-hover:translate-x-1 transition-all" aria-hidden />
      </div>
    </Link>
  ),
  'goal-progress':  () => <GoalProgressCard />,
  'recent-lectures': ({ recentLectures }) => (
    <div className="isp-card p-5 lg:p-6" data-testid="recent-lectures-card">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-lg sm:text-xl font-semibold text-[var(--isp-heading)]">Recent Lectures</h2>
        <Link to="/lectures">
          <Button variant="ghost" size="sm" className="rounded-xl text-xs font-body text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]" data-testid="view-all-lectures">
            View All
          </Button>
        </Link>
      </div>
      <div className="space-y-3">
        {recentLectures.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-14 h-14 mx-auto bg-[var(--isp-glass-inner)] flex items-center justify-center mb-3">
              <GraduationCap className="w-7 h-7 text-[var(--isp-muted)]" aria-hidden />
            </div>
            <p className="font-body text-[var(--isp-body)] text-sm mb-4">No lectures yet. Record your first!</p>
            <Link to="/lectures">
              <Button className="gap-2 isp-btn-primary font-body">
                <Plus className="w-4 h-4" /> Record Lecture
              </Button>
            </Link>
          </div>
        ) : (
          recentLectures.map((lecture) => (
            <div
              key={lecture.id}
              className="flex items-start justify-between p-4 rounded-xl bg-[var(--isp-glass-inner)] hover:bg-[var(--isp-glass-inner-hover)] transition-all duration-200 border border-[var(--isp-glass-border)] hover:-translate-y-0.5 hover:shadow-md"
              data-testid={`lecture-${lecture.id}`}
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-body font-medium text-[var(--isp-heading)] truncate text-sm">{lecture.title}</h3>
                {lecture.topic && <p className="font-body text-xs text-[var(--isp-body)] mt-0.5 truncate">{lecture.topic}</p>}
                <p className="text-[10px] font-body text-[var(--isp-body)]/60 mt-1.5">
                  {format(new Date(lecture.date), 'PPp')}
                </p>
              </div>
              {lecture.key_points && lecture.key_points.length > 0 && (
                <div className="ml-3 px-2.5 py-1 bg-[var(--isp-glass-inner)] text-[var(--isp-heading)] rounded-lg text-[10px] font-body font-medium flex items-center gap-1">
                  <Zap className="w-3 h-3" aria-hidden /> {lecture.key_points.length} pts
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  ),
  'upcoming-exams': ({ upcomingExams }) => (
    <div className="isp-card p-5 lg:p-6" data-testid="upcoming-exams-card">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-lg sm:text-xl font-semibold text-[var(--isp-heading)]">Upcoming Exams</h2>
        <Link to="/schedule">
          <Button variant="ghost" size="sm" className="rounded-xl text-xs font-body text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]" data-testid="view-schedule">
            View All
          </Button>
        </Link>
      </div>
      <div className="space-y-3">
        {upcomingExams.length === 0 ? (
          <div className="text-center py-8" data-testid="exams-empty">
            <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300" aria-hidden />
            <p className="text-sm font-body text-[var(--isp-body)] mb-3">No exams scheduled yet.</p>
            <Link to="/schedule">
              <Button size="sm" className="gap-1.5 isp-btn-primary font-body" data-testid="exams-empty-cta">
                <Plus className="w-3.5 h-3.5" /> Add an exam
              </Button>
            </Link>
            <p className="text-[10px] text-[var(--isp-muted)] mt-2">Adding exams unlocks the countdown widget + exam-mode quizzes.</p>
          </div>
        ) : (
          upcomingExams.map((exam) => {
            const daysUntil = Math.ceil((new Date(exam.exam_date) - new Date()) / (1000 * 60 * 60 * 24));
            return (
              <div
                key={exam.id}
                className="p-4 rounded-xl bg-[var(--isp-glass-inner)] hover:bg-[var(--isp-glass-inner-hover)] transition-all border border-[var(--isp-glass-border)] hover:-translate-y-0.5 hover:shadow-md"
                data-testid={`exam-${exam.id}`}
              >
                <h3 className="font-body font-medium text-[var(--isp-heading)] truncate text-sm">{exam.title}</h3>
                <div className="flex items-center gap-2 mt-1.5 text-xs font-body text-[var(--isp-body)]">
                  <Calendar className="w-3 h-3" aria-hidden />
                  {format(new Date(exam.exam_date), 'PP')}
                </div>
                {daysUntil >= 0 && (
                  <span className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${
                    daysUntil <= 3 ? 'bg-red-100 text-red-700' :
                    daysUntil <= 7 ? 'bg-amber-100 text-amber-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    <Clock className="w-3 h-3" aria-hidden />
                    {daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  ),
};

export default TodayTab;
